import { Controller } from "@hotwired/stimulus";
import i18n from "../../i18n/translations";
import { resolveCssColor } from "../../utils/chart_colors.js";

export default class extends Controller {
  static targets = [
    "balanceDisplay",
    "attachmentSection",
    "balancesContainer",
    "balancesHeader",
    "yearlyEvolutionChart",
    "leaveDistributionChart",
    "expiryDistributionChart",
    "staffDistributionChart",
    "leaveTypeDistributionChart",
  ];

  // Résout les couleurs CSS `var(--x)` en valeurs réelles (utils/chart_colors).
  resolveCssColors(colors) {
    return resolveCssColor(colors);
  }

  connect() {
    console.log("Leave balance controller connected");
    const staffSelect = this.element.querySelector(
      'select[name="leave_request[requester_id]"]',
    );
    if (staffSelect && staffSelect.selectedIndex >= 0) {
      this.offerLeaveTypesOf(
        staffSelect.options[staffSelect.selectedIndex].dataset.schoolId,
      );
    }
    this.updateBalance();

    // Initialize charts if they exist on the page
    if (
      this.hasYearlyEvolutionChartTarget &&
      this.hasLeaveDistributionChartTarget
    ) {
      this.initializeHistoryCharts();
    }

    if (this.hasExpiryDistributionChartTarget) {
      this.initializeExpiryChart();
    }

    if (
      this.hasStaffDistributionChartTarget &&
      this.hasLeaveTypeDistributionChartTarget
    ) {
      this.initializeHighBalanceCharts();
    }
  }

  updateBalance() {
    const leaveTypeSelect = this.element.querySelector(
      'select[name="leave_request[leave_type_id]"]',
    );
    if (!leaveTypeSelect) return;

    const leaveTypeId = leaveTypeSelect.value;

    // Show/hide attachment section based on leave type data attribute
    if (this.hasAttachmentSectionTarget) {
      const selectedOption =
        leaveTypeSelect.options[leaveTypeSelect.selectedIndex];
      if (selectedOption) {
        const requiresAttachment =
          selectedOption.dataset.requiresAttachment === "true";
        if (requiresAttachment) {
          this.attachmentSectionTarget.classList.remove("hidden");
        } else {
          this.attachmentSectionTarget.classList.add("hidden");
        }
      }
    }

    // Toggle balance display for the selected leave type in the form
    if (this.hasBalanceDisplayTarget) {
      const balanceDisplays = this.balanceDisplayTarget.querySelectorAll(
        "[data-leave-type-id]",
      );
      balanceDisplays.forEach((display) => {
        if (display.dataset.leaveTypeId === leaveTypeId) {
          display.classList.remove("hidden");
        } else {
          display.classList.add("hidden");
        }
      });
    }
  }

  updateStaffMember() {
    // Get the selected staff member ID
    const staffSelect = this.element.querySelector(
      'select[name="leave_request[requester_id]"]',
    );
    if (!staffSelect) return;

    const staffMemberId = staffSelect.value;
    if (!staffMemberId) return;

    const selectedOption = staffSelect.options[staffSelect.selectedIndex];
    const staffName = selectedOption.text;
    this.offerLeaveTypesOf(selectedOption.dataset.schoolId);

    // Fetch with locale support
    const locale = document.documentElement.lang || "fr";
    const url = `/${locale}/schools/rh/staff/${staffMemberId}/leave_balances`;

    fetch(url)
      .then((response) => response.json())
      .then((data) => {
        // Update the header title in the sidebar for better UX
        if (this.hasBalancesHeaderTarget) {
          const periodSuffix = data.period_name ? ` ${data.period_name}` : "";
          this.balancesHeaderTarget.textContent =
            locale === "fr"
              ? `Soldes de congés${periodSuffix} de ${staffName}`
              : `${staffName}'s leave balances${periodSuffix}`;
        }

        // Update the leave balances display in the sidebar
        if (this.hasBalancesContainerTarget) {
          this.renderLeaveBalances(
            this.balancesContainerTarget,
            data.leave_balances,
          );
        }

        // Update the leave balances display in the form's "Available balance" field
        this.renderFormBalances(data.leave_balances);

        // Refresh the currently selected balance display
        this.updateBalance();
      })
      .catch((error) => console.error("Error fetching leave balances:", error));
  }

  // With every school of a complex on screen, a person is offered the leave
  // types of the school their leave is kept in.
  offerLeaveTypesOf(schoolId) {
    const typeSelect = this.element.querySelector(
      'select[name="leave_request[leave_type_id]"]',
    );
    if (!typeSelect) return;
    // Nobody chosen yet: no type is offered, rather than each one once per school.
    const bySchool = Array.from(typeSelect.options).some(
      (option) => option.dataset.schoolId,
    );
    if (!bySchool) return;

    Array.from(typeSelect.options).forEach((option) => {
      const other =
        option.dataset.schoolId && option.dataset.schoolId !== schoolId;
      // SlimSelect dresses this select: it reads `display` and `disabled`.
      option.hidden = other;
      option.disabled = other;
      option.style.display = other ? "none" : "";
      if (other && option.selected) typeSelect.value = "";
    });
  }

  renderFormBalances(balances) {
    if (!this.hasBalanceDisplayTarget) return;

    const container = this.balanceDisplayTarget.querySelector(".mt-1.py-2");
    if (!container) return;

    container.innerHTML = "";

    if (!balances || balances.length === 0) {
      container.innerHTML = `<p class="text-sm text-gray-500">${i18n.t("rh.leave_requests.no_balances")}</p>`;
      return;
    }

    balances.forEach((balance) => {
      const colorClass =
        balance.remaining_days > 0
          ? "bg-cta-shared text-cta-darker"
          : "bg-red-100 text-red-800";
      const div = document.createElement("div");
      div.className = "hidden";
      div.dataset.leaveTypeId = balance.leave_type_id;

      div.innerHTML = `
        <span class="px-2 py-1 text-sm font-medium rounded-sm ${colorClass}">
          ${Math.round(balance.remaining_days)} ${i18n.t("rh.common.days")}
        </span>
      `;
      container.appendChild(div);
    });
  }

  renderLeaveBalances(container, balances) {
    // Clear the container
    container.innerHTML = "";

    if (!balances || balances.length === 0) {
      container.innerHTML = `<p class="text-sm text-gray-500">${i18n.t("rh.leave_requests.no_balances")}</p>`;
      return;
    }

    // Format 1: Table display (for full page view)
    if (container.classList.contains("full-view")) {
      // Create a table to display the balances
      const table = document.createElement("table");
      table.className = "table table-striped table-sm";

      // Create the table header
      const thead = document.createElement("thead");
      const headerRow = document.createElement("tr");

      const headers = [
        i18n.t("rh.leave_requests.leave_type"),
        i18n.t("rh.leave_balances.entitled_days_label"),
        i18n.t("rh.leave_balances.carried_over_days_label"),
        i18n.t("rh.leave_balances.taken_days_label"),
        i18n.t("rh.leave_balances.remaining_days_label"),
      ];
      headers.forEach((header) => {
        const th = document.createElement("th");
        th.textContent = header;
        headerRow.appendChild(th);
      });

      thead.appendChild(headerRow);
      table.appendChild(thead);

      // Create the table body
      const tbody = document.createElement("tbody");

      balances.forEach((balance) => {
        const row = document.createElement("tr");

        // Type de congé
        const typeCell = document.createElement("td");
        const badge = document.createElement("span");
        badge.className = "badge";
        badge.style.backgroundColor = balance.leave_type.color_code;
        badge.textContent = balance.leave_type.name;
        typeCell.appendChild(badge);
        row.appendChild(typeCell);

        // Jours attribués
        const entitledCell = document.createElement("td");
        entitledCell.textContent = balance.entitled_days;
        row.appendChild(entitledCell);

        // Jours reportés
        const carriedOverCell = document.createElement("td");
        carriedOverCell.textContent =
          balance.carried_over_days_from_previous_year;
        row.appendChild(carriedOverCell);

        // Jours pris
        const takenCell = document.createElement("td");
        takenCell.textContent = balance.taken_days;
        row.appendChild(takenCell);

        // Jours restants
        const remainingCell = document.createElement("td");
        remainingCell.textContent = Math.round(balance.remaining_days);
        remainingCell.style.fontWeight = "bold";
        row.appendChild(remainingCell);

        tbody.appendChild(row);
      });

      table.appendChild(tbody);
      container.appendChild(table);
    }
    // Format 2: Compact display (for sidebar or form view)
    else {
      let html = '<div class="mt-2 space-y-2">';

      balances.forEach((balance) => {
        const colorClass =
          balance.remaining_days > 0
            ? "bg-cta-shared text-cta-darker"
            : "bg-red-100 text-red-800";

        html += `
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="h-3 w-3 rounded-full mr-2" style="background-color: ${balance.leave_type.color_code}"></div>
              <span class="text-sm text-gray-700">${balance.leave_type.name}</span>
            </div>
            <span class="px-2 py-1 text-xs font-medium rounded-sm ${colorClass}">
              ${Math.round(balance.remaining_days)} ${i18n.t("rh.common.days")}
            </span>
          </div>
        `;
      });

      html += "</div>";
      container.innerHTML = html;
    }
  }

  initializeHistoryCharts() {
    // Les données doivent être fournies via des attributs data sur les éléments cibles
    if (!this.yearlyEvolutionChartTarget || !this.leaveDistributionChartTarget)
      return;

    const yearlyCtx = this.yearlyEvolutionChartTarget.getContext("2d");
    const distributionCtx = this.leaveDistributionChartTarget.getContext("2d");

    // Récupérer les données depuis les attributs data
    const years = JSON.parse(
      this.yearlyEvolutionChartTarget.dataset.years || "[]",
    );
    const datasets = JSON.parse(
      this.yearlyEvolutionChartTarget.dataset.datasets || "[]",
    );
    const remainingDaysLabel =
      this.yearlyEvolutionChartTarget.dataset.remainingDaysLabel ||
      i18n.t("rh.leave_balances.remaining_days_label");
    const yearLabel =
      this.yearlyEvolutionChartTarget.dataset.yearLabel ||
      i18n.t("rh.common.year");

    // Créer le graphique d'évolution annuelle
    new Chart(yearlyCtx, {
      type: "line",
      data: {
        labels: years,
        datasets: datasets,
      },
      options: {
        responsive: true,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: remainingDaysLabel,
            },
          },
          x: {
            title: {
              display: true,
              text: yearLabel,
            },
          },
        },
      },
    });

    // Récupérer les données pour le graphique de distribution
    const distributionLabels = JSON.parse(
      this.leaveDistributionChartTarget.dataset.labels || "[]",
    );
    const distributionData = JSON.parse(
      this.leaveDistributionChartTarget.dataset.data || "[]",
    );
    const distributionColors = this.resolveCssColors(JSON.parse(
      this.leaveDistributionChartTarget.dataset.colors || "[]",
    ));
    const distributionTitle =
      this.leaveDistributionChartTarget.dataset.title ||
      i18n.t("rh.leave_balances.leave_distribution");

    // Créer le graphique de distribution
    new Chart(distributionCtx, {
      type: "doughnut",
      data: {
        labels: distributionLabels,
        datasets: [
          {
            data: distributionData,
            backgroundColor: distributionColors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true,
            position: "bottom",
          },
          title: {
            display: true,
            text: distributionTitle,
          },
        },
      },
    });
  }

  initializeExpiryChart() {
    if (!this.hasExpiryDistributionChartTarget) return;

    const ctx = this.expiryDistributionChartTarget.getContext("2d");

    // Récupérer les données depuis les attributs data
    const labels = JSON.parse(
      this.expiryDistributionChartTarget.dataset.labels || "[]",
    );
    const data = JSON.parse(
      this.expiryDistributionChartTarget.dataset.data || "[]",
    );
    const colors = this.resolveCssColors(JSON.parse(
      this.expiryDistributionChartTarget.dataset.colors || "[]",
    ));
    const balancesLabel =
      this.expiryDistributionChartTarget.dataset.balancesLabel ||
      i18n.t("rh.leave_balances.number_of_balances");

    // Créer le graphique
    new Chart(ctx, {
      type: "bar",
      data: {
        labels: labels,
        datasets: [
          {
            label: balancesLabel,
            data: data,
            backgroundColor: colors,
            borderColor: colors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        scales: {
          y: {
            beginAtZero: true,
            ticks: {
              precision: 0,
            },
          },
        },
      },
    });
  }

  initializeHighBalanceCharts() {
    if (
      !this.hasStaffDistributionChartTarget ||
      !this.hasLeaveTypeDistributionChartTarget
    )
      return;

    const staffCtx = this.staffDistributionChartTarget.getContext("2d");
    const leaveTypeCtx = this.leaveTypeDistributionChartTarget.getContext("2d");

    // Récupérer les données pour le graphique de distribution par membre du personnel
    const staffNames = JSON.parse(
      this.staffDistributionChartTarget.dataset.names || "[]",
    );
    const staffRemainingDays = JSON.parse(
      this.staffDistributionChartTarget.dataset.days || "[]",
    );
    const remainingDaysLabel =
      this.staffDistributionChartTarget.dataset.remainingDaysLabel ||
      i18n.t("rh.leave_balances.remaining_days_label");

    // Créer le graphique de distribution par membre du personnel
    new Chart(staffCtx, {
      type: "bar",
      data: {
        labels: staffNames,
        datasets: [
          {
            label: remainingDaysLabel,
            data: staffRemainingDays,
            backgroundColor: "rgba(54, 162, 235, 0.6)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        scales: {
          y: {
            beginAtZero: true,
          },
        },
      },
    });

    // Récupérer les données pour le graphique de distribution par type de congé
    const leaveTypeNames = JSON.parse(
      this.leaveTypeDistributionChartTarget.dataset.names || "[]",
    );
    const leaveTypeRemainingDays = JSON.parse(
      this.leaveTypeDistributionChartTarget.dataset.days || "[]",
    );
    const leaveTypeColors = this.resolveCssColors(JSON.parse(
      this.leaveTypeDistributionChartTarget.dataset.colors || "[]",
    ));

    // Créer le graphique de distribution par type de congé
    new Chart(leaveTypeCtx, {
      type: "pie",
      data: {
        labels: leaveTypeNames,
        datasets: [
          {
            data: leaveTypeRemainingDays,
            backgroundColor: leaveTypeColors,
            borderWidth: 1,
          },
        ],
      },
      options: {
        responsive: true,
        plugins: {
          legend: {
            position: "bottom",
          },
        },
      },
    });
  }
}
